import { useCallback, useMemo, useState } from "react";
import { Box, Chip, IconButton, TextField, Tooltip } from "@mui/material";
import { Print } from "@mui/icons-material";
import { DataTable, type Column } from "@/components/DataTable";
import type { Indicador } from "@/components/Indicadores";
import { EscalaEspelhoView } from "@/components/escala/EscalaEspelhoView";
import { RelatorioLayout } from "@/components/escala/RelatorioLayout";
import { FiltroRelatorio } from "@/components/escala/RelatorioFiltroCampos";
import { porNome, useRelatorio } from "@/components/escala/useRelatorio";
import { distintos, formatPercentual, plural } from "@/components/escala/relatorio";
import {
  EscalaCaracteristicaLabels,
  EscalaTipoLabels,
  relColaboradoresPorEscala,
  rotuloLoja,
  type EscalaCaracteristica,
  type EscalaTipo,
  type LinhaColaboradorEscala,
  type RelatorioFiltro,
} from "@/api/escala.service";


interface FiltroColaboradoresPorEscala extends RelatorioFiltro {
  colaborador: string;
}

// Foto do cadastro: sem ano/mês, o Method só recebe loja, cargo e colaborador.
const SEM_COMPETENCIA: FiltroColaboradoresPorEscala = { ano: 0, mes: 0, colaborador: "" };
const buscarSemCompetencia = ({ loja_id, cargo, colaborador }: FiltroColaboradoresPorEscala, clientId: string) =>
  relColaboradoresPorEscala({ loja_id, cargo, colaborador: colaborador || undefined }, clientId);

function indicadoresVinculo(linhas: LinhaColaboradorEscala[]): [Indicador, Indicador, Indicador, Indicador] {
  const vinculados = linhas.filter((l) => l.escala_id).length;
  const semEscala = linhas.length - vinculados;
  const porTipo = new Map<string, number>();
  for (const l of linhas) if (l.tipo) porTipo.set(l.tipo, (porTipo.get(l.tipo) ?? 0) + 1);
  const comum = [...porTipo.entries()].sort((a, b) => b[1] - a[1])[0];
  // Rótulo inteiro: cortado no "—", os dois 5x2 e as folgas fixas ficavam iguais.
  const rotuloTipo = (tipo: string) => EscalaTipoLabels[tipo as EscalaTipo] ?? tipo;
  return [
    { titulo: "Colaboradores ativos", valor: linhas.length, nota: `em ${plural(distintos(linhas, (l) => l.loja_id), "loja")}` },
    {
      titulo: "Vinculados a uma escala",
      valor: vinculados,
      nota: linhas.length ? `${formatPercentual(vinculados / linhas.length)} da equipe` : "nenhum colaborador no filtro",
    },
    {
      titulo: "Sem escala",
      valor: semEscala,
      nota: semEscala ? "precisam ser vinculados a uma escala" : "todos vinculados",
      tom: semEscala ? "atencao" : "bom",
    },
    {
      titulo: "Escalas ativas",
      valor: distintos(linhas, (l) => l.escala_id),
      nota: comum ? `mais comum: ${rotuloTipo(comum[0])} (${plural(comum[1], "colaborador", "colaboradores")})` : "nenhuma escala ativa",
    },
  ];
}

// Foto do cadastro atual, sem competência: a escala vigente de cada colaborador ativo.
export function EscalaRelColaboradoresPorEscalaTab() {
  const r = useRelatorio<LinhaColaboradorEscala, FiltroColaboradoresPorEscala>({
    buscar: buscarSemCompetencia,
    ordenar: porNome,
    mensagemErro: "Erro ao carregar o relatório de colaboradores por escala",
    inicial: SEM_COMPETENCIA,
  });
  const indicadores = useMemo(() => indicadoresVinculo(r.linhas), [r.linhas]);
  const [imprimindoDe, setImprimindoDe] = useState<LinhaColaboradorEscala | null>(null);
  const finalizarImpressao = useCallback(() => setImprimindoDe(null), []);

  const caracteristicaLabel = (l: LinhaColaboradorEscala) =>
    l.caracteristica ? EscalaCaracteristicaLabels[l.caracteristica as EscalaCaracteristica] : "";
  const tipoLabel = (l: LinhaColaboradorEscala) => (l.tipo ? EscalaTipoLabels[l.tipo as EscalaTipo] ?? l.tipo : "");

  const columns: Column<LinhaColaboradorEscala>[] = [
    { key: "matricula", label: "Matrícula" },
    { key: "nome", label: "Nome" },
    { key: "loja_id", label: "Loja", render: (l) => rotuloLoja(r.lojas, l.loja_id), exportValue: (l) => rotuloLoja(r.lojas, l.loja_id) },
    {
      key: "escala_nome",
      label: "Escala",
      render: (l) => (l.escala_nome ? l.escala_nome : <Chip size="small" label="Sem escala" color="warning" />),
      exportValue: (l) => l.escala_nome ?? "Sem escala",
    },
    { key: "tipo", label: "Tipo", render: tipoLabel, exportValue: tipoLabel },
    { key: "caracteristica", label: "Característica", render: caracteristicaLabel, exportValue: caracteristicaLabel },
  ];

  return (
    <RelatorioLayout
      carregando={r.carregando}
      filtro={
        <FiltroRelatorio estado={r} comCompetencia={false}>
          <TextField
            label="Colaborador"
            size="small"
            fullWidth
            value={r.inputValues.colaborador}
            onChange={(e) => r.setInputValues((v) => ({ ...v, colaborador: e.target.value }))}
          />
        </FiltroRelatorio>
      }
      indicadores={indicadores}
      exportacao={{ colunas: columns, linhas: r.ordenadas, arquivo: "colaboradores-por-escala", aba: "Colaboradores por Escala" }}
    >
      <DataTable
        columns={columns}
        {...r.tabela}
        actions={(l) =>
          l.gerada_aprovada_id && (
            <Tooltip title="Imprimir a escalinha do colaborador">
              <Box component="span">
                <IconButton
                  aria-label={`Imprimir a escalinha de ${l.nome}`}
                  disabled={imprimindoDe?.colaborador_id === l.colaborador_id}
                  onClick={() => setImprimindoDe(l)}
                >
                  <Print />
                </IconButton>
              </Box>
            </Tooltip>
          )
        }
      />
      {/* Carrega o espelho aprovado fora da tela e dispara a impressão sozinho, como o atalho da aba Geradas. */}
      {imprimindoDe?.gerada_aprovada_id && (
        <Box sx={{ position: "fixed", top: 0, left: "-9999px", width: 0, height: 0, overflow: "hidden" }} inert>
          <EscalaEspelhoView
            key={`${imprimindoDe.gerada_aprovada_id}-${imprimindoDe.colaborador_id}`}
            geradaId={imprimindoDe.gerada_aprovada_id}
            autoImprimir
            autoImprimirColaboradorId={imprimindoDe.colaborador_id}
            onImpressaoConcluida={finalizarImpressao}
          />
        </Box>
      )}
    </RelatorioLayout>
  );
}
